import * as Yup from 'yup'
import {ISO3166_ALPHA2_CODES} from './countrySelect'

/** Pass `useI18n().t` from a component inside `I18nProvider`. */
export type TranslateFn = (key: string) => string

export const getOrganizationSignupSchema = (t: TranslateFn) =>
  Yup.object().shape({
    organization_name: Yup.string()
      .trim()
      .min(2, t('validation.min2chars'))
      .required(t('validation.required')),
    first_name: Yup.string().required(t('validation.required')),
    last_name: Yup.string().required(t('validation.required')),
    email: Yup.string()
      .required(t('validation.required'))
      .email(t('validation.emailInvalid')),
    accept_terms: Yup.boolean()
      .oneOf([true], t('validation.acceptTerms'))
      .required(t('validation.required')),
    password: Yup.string()
      .required(t('validation.required'))
      .min(8, t('validation.passwordMin')),
    password_confirmation: Yup.string().test(
      'passwords-match',
      t('validation.passwordsMatch'),
      function (value) {
        return value === this.resolve(Yup.ref('password')) || value === null
      },
    ),
    promo_invite_token: Yup.string()
      .nullable()
      .test(
        'invite-len',
        t('validation.promoInviteInvalid'),
        (v) => !v || /^[a-f0-9]{64}$/i.test(v),
      ),
  })

export const getSignupSchema = (t: TranslateFn) =>
  Yup.object().shape({
    first_name: Yup.string().required(t('validation.required')),
    last_name: Yup.string().required(t('validation.required')),
    email: Yup.string()
      .required(t('validation.required'))
      .email(t('validation.emailInvalid')),
    accept_terms: Yup.boolean()
      .oneOf([true], t('validation.acceptTerms'))
      .required(t('validation.required')),
    password: Yup.string()
      .required(t('validation.required'))
      .min(8, t('validation.passwordMin')),
    password_confirmation: Yup.string().test(
      'passwords-match',
      t('validation.passwordsMatch'),
      function (value) {
        return value === this.resolve(Yup.ref('password')) || value === null
      },
    ),
  })

export const getLoginSchema = (t: TranslateFn) =>
  Yup.object().shape({
    email: Yup.string()
      .required(t('validation.required'))
      .email(t('validation.emailInvalid')),
    password: Yup.string()
      .required(t('validation.required'))
      .min(8, t('validation.passwordMin')),
  })

export const getForgottenSchema = (t: TranslateFn) =>
  Yup.object().shape({
    email: Yup.string()
      .required(t('validation.required'))
      .email(t('validation.emailInvalid')),
  })

export const getPasswordResetSchema = (t: TranslateFn) =>
  Yup.object().shape({
    password: Yup.string()
      .required(t('validation.required'))
      .min(8, t('validation.passwordMin')),
    password_confirmation: Yup.string().test(
      'passwords-match',
      t('validation.passwordsMatch'),
      function (value) {
        return value === this.resolve(Yup.ref('password')) || value === null
      },
    ),
  })

export const getProfileUpdateSchema = (t: TranslateFn) =>
  Yup.object().shape({
    first_name: Yup.string().required(t('validation.required')),
    last_name: Yup.string().required(t('validation.required')),
    email: Yup.string()
      .required(t('validation.required'))
      .email(t('validation.emailInvalid')),
  })

export const getPasswordUpdateSchema = (t: TranslateFn) =>
  Yup.object().shape({
    old_password: Yup.string().required(t('validation.required')),
    password: Yup.string()
      .required(t('validation.required'))
      .min(8, t('validation.passwordMin')),
    password_confirmation: Yup.string().test(
      'passwords-match',
      t('validation.passwordsMatch'),
      function (value) {
        return value === this.resolve(Yup.ref('password')) || value === null
      },
    ),
  })

const companyFields = (t: TranslateFn) => ({
  company_name: Yup.string()
    .trim()
    .min(2, t('validation.min2chars'))
    .max(255, t('validation.max255'))
    .required(t('validation.companyNameRequired')),

  eik: Yup.string()
    .trim()
    .min(3, t('validation.min3chars'))
    .max(40, t('validation.max40'))
    .matches(/^[A-Za-z0-9\-./\s]+$/, t('validation.companyIdChars'))
    .required(t('validation.companyIdRequired')),

  vat_number: Yup.string()
    .transform((v) => (typeof v === 'string' ? v.trim() : v))
    .nullable()
    .notRequired()
    .test('vat-format', t('validation.vatInvalid'), (value) => {
      if (!value) return true
      const x = value.replace(/\s/g, '').toUpperCase()
      return /^[A-Z0-9*+.-]{4,20}$/.test(x)
    }),

  mol: Yup.string()
    .trim()
    .min(2, t('validation.min2chars'))
    .max(255, t('validation.max255'))
    .required(t('validation.molRequired')),

  country: Yup.string()
    .trim()
    .length(2, t('validation.countryIso'))
    .oneOf(ISO3166_ALPHA2_CODES, t('validation.countryCode'))
    .required(t('validation.countryRequired')),

  city: Yup.string()
    .trim()
    .min(2, t('validation.cityMin'))
    .max(120, t('validation.max120'))
    .required(t('validation.cityRequired')),

  postal_code: Yup.string()
    .trim()
    .min(3, t('validation.postalMin'))
    .max(20, t('validation.postalMaxLen'))
    .matches(/^[A-Za-z0-9\-\s]+$/, t('validation.postalFormat'))
    .required(t('validation.postalRequired')),

  address_line: Yup.string()
    .trim()
    .min(3, t('validation.addressMin'))
    .max(255, t('validation.max255'))
    .required(t('validation.addressRequired')),
})

export const getCompanyUpdateSchema = (t: TranslateFn) =>
  Yup.object(companyFields(t))
